import { isExcludedProperty } from '@jupiterone/jupiter-web-toolkit';
import { InspectEdge } from '../../types';

const TAG_KEY = 'tag.';

export enum TabIdentifier {
  Properties = 'properties',
  Tags = 'tags',
  Metadata = 'metadata',
}

export enum PromptMode {
  Closed,
  Edit,
  Delete,
}

interface TabData {
  identifier: TabIdentifier;
  label: string;
  properties: string[];
}

export interface RelationshipDrawerState {
  tabs: TabData[];
  selectedTab: TabIdentifier;
}

type Reducer<T> = (state: RelationshipDrawerState) => T;

const isTag = (property: string): boolean => {
  return property.indexOf(TAG_KEY) === 0;
};

const isMetadata = (propertyName: string): boolean => {
  return propertyName[0] === '_';
};

export const initialState: RelationshipDrawerState = {
  tabs: [
    {
      identifier: TabIdentifier.Properties,
      label: 'Properties',
      properties: [],
    },
    { identifier: TabIdentifier.Tags, label: 'Tags', properties: [] },
    { identifier: TabIdentifier.Metadata, label: 'Metadata', properties: [] },
  ],
  selectedTab: TabIdentifier.Properties,
};

export const getActiveProperties: Reducer<string[]> = (state) => {
  const activeTab = state.tabs.find(
    (tab) => tab.identifier === state.selectedTab
  );
  return activeTab!.properties;
};

export const selectTab = (
  tab: TabIdentifier
): Reducer<RelationshipDrawerState> => (state) => ({
  ...state,
  selectedTab: tab,
});

const assignProperties = (
  properties: string[],
  metadata: string[],
  tags: string[]
): Reducer<RelationshipDrawerState> => (state) => ({
  ...state,
  tabs: state.tabs.map((tab) => {
    let propertiesForThisTab: string[] = [];

    switch (tab.identifier) {
      case TabIdentifier.Properties: {
        propertiesForThisTab = properties;
        break;
      }
      case TabIdentifier.Metadata: {
        propertiesForThisTab = metadata;
        break;
      }
      case TabIdentifier.Tags: {
        propertiesForThisTab = tags;
        break;
      }
      default:
        break;
    }

    return {
      ...tab,
      properties: propertiesForThisTab,
    };
  }),
});

export const readAndSortProperties = (
  edge: InspectEdge
): Reducer<RelationshipDrawerState> => {
  const properties: string[] = [];
  const metadata: string[] = [];
  const tags: string[] = [];

  if (edge.properties) {
    Object.keys(edge.properties)
      .sort()
      .forEach((key: string) => {
        if (isExcludedProperty(key)) return;

        if (isTag(key)) {
          tags.push(key);
        } else if (isMetadata(key)) {
          metadata.push(key);
        } else {
          properties.push(key);
        }
      });
  }

  return assignProperties(properties, metadata, tags);
};
